import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  MenuToggle,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  SelectList,
  SelectOption,
  Switch,
  TextInput,
} from '@patternfly/react-core';
import CenteredSpinner from '@app/CenteredSpinner';
import { useApiFetch } from '../../../api';
import { useDomainRegistrationPlatforms } from '../../../hooks/useDomainRegistrationPlatforms';
import { parseRegistrarDate } from '../../../utils/parseRegistrarDate';

interface ImportDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HOSTNAME_RE = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

interface ApiErrorBody {
  status?: 'success' | 'fail' | 'error';
  message?: string;
  data?: unknown;
}

function ImportDomainModal({ isOpen, onClose }: ImportDomainModalProps) {
  const apiFetch = useApiFetch();
  const { platforms, loading: platformsLoading, error: platformsError } = useDomainRegistrationPlatforms();

  const [name, setName] = useState('');
  const [platformId, setPlatformId] = useState<number | null>(null);
  const [createdAt, setCreatedAt] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [vanishesAt, setVanishesAt] = useState<string>('');
  const [addAnother, setAddAnother] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [topLevelError, setTopLevelError] = useState<string | null>(null);
  const [isPlatformSelectOpen, setIsPlatformSelectOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setPlatformId(null);
      setCreatedAt('');
      setExpiresAt('');
      setVanishesAt('');
      setAddAnother(false);
      setIsSubmitting(false);
      setFieldErrors({});
      setTopLevelError(null);
      setIsPlatformSelectOpen(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const onPlatformSelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined,
  ) => {
    const idNum = typeof value === 'number' ? value : Number(value);
    setPlatformId(Number.isFinite(idNum) ? idNum : null);
    setIsPlatformSelectOpen(false);
  };

  const platformsEmpty = !platformsLoading && !platformsError && platforms.length === 0;
  const canSubmit = !platformsLoading && !platformsError && !platformsEmpty && !isSubmitting;

  const formatYmd = (d: Date): string => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const makeDateChangeHandler =
    (setter: (value: string) => void) => (_event: React.FormEvent<HTMLInputElement>, raw: string, date?: Date) => {
      if (date !== undefined && !Number.isNaN(date.getTime())) {
        setter(formatYmd(date));
      } else {
        setter(raw);
      }
    };

  const handleDateParse = (input: string): Date => {
    const fromRegistrar = parseRegistrarDate(input);
    if (fromRegistrar !== null) return fromRegistrar;

    const trimmed = input.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [y, m, d] = trimmed.split('-').map(Number);
      const native = new Date(y, m - 1, d);
      if (
        !Number.isNaN(native.getTime()) &&
        native.getFullYear() === y &&
        native.getMonth() === m - 1 &&
        native.getDate() === d
      ) {
        return native;
      }
    }

    return new Date('Invalid Date');
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    const trimmedName = name.trim().toLowerCase();
    if (!trimmedName) {
      errors.name = 'Domain name is required';
    } else if (!HOSTNAME_RE.test(trimmedName)) {
      errors.name = 'Domain name must be a valid hostname (e.g. example.com)';
    }

    if (platformId === null) {
      errors.domain_registration_platform_id = 'A registrar must be selected';
    }

    if (!createdAt) {
      errors.created_at = 'Registration date is required';
    }

    if (!expiresAt) {
      errors.expires_at = 'Expiration date is required';
    }

    if (vanishesAt !== '' && expiresAt !== '' && vanishesAt < expiresAt) {
      errors.vanishes_at = 'Grace-window end must be on or after the expiration date';
    }

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setTopLevelError(null);
      return;
    }

    setIsSubmitting(true);
    setFieldErrors({});
    setTopLevelError(null);

    try {
      const body: Record<string, unknown> = {
        name: name.trim().toLowerCase(),
        domain_registration_platform_id: platformId,
        created_at: `${createdAt} 00:00:00`,
        expires_at: `${expiresAt} 00:00:00`,
      };
      if (vanishesAt !== '') {
        body.vanishes_at = `${vanishesAt} 00:00:00`;
      }

      const response = await apiFetch('/datacenters/domain-names', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const responseBody = (await response.json().catch(() => null)) as ApiErrorBody | null;

      if (!response.ok || responseBody?.status !== 'success') {
        const errorData = responseBody?.data;
        if (
          errorData !== null &&
          typeof errorData === 'object' &&
          !Array.isArray(errorData) &&
          Object.keys(errorData as Record<string, unknown>).length > 0
        ) {
          setFieldErrors(errorData as Record<string, string>);
          setTopLevelError(null);
        } else {
          setTopLevelError(responseBody?.message ?? 'Import failed');
          setFieldErrors({});
        }
        return;
      }

      if (addAnother) {
        setName('');
        setCreatedAt('');
        setExpiresAt('');
        setVanishesAt('');
        setFieldErrors({});
        setTopLevelError(null);
      } else {
        onClose();
      }
    } catch {
      setTopLevelError('Network error — please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedPlatformName =
    platformId !== null
      ? (platforms.find((p) => p.id === platformId)?.name ?? 'Select a registrar')
      : 'Select a registrar';

  const renderBody = () => {
    if (platformsLoading) {
      return <CenteredSpinner />;
    }

    if (platformsError) {
      return (
        <Alert variant="danger" isInline title="Unable to load registrars">
          {platformsError}
        </Alert>
      );
    }

    return (
      <>
        <Alert variant="warning" isInline title="Ancalagon staff only" className="pf-v6-u-mb-md">
          This import feature is a temporary feature that must only be used by Ancalagon Staff and will otherwise be
          ignored.
        </Alert>

        {platformsEmpty && (
          <Alert variant="warning" isInline title="No registrars available" className="pf-v6-u-mb-md">
            No domain registration platforms are configured. Please contact the Ancalagon tech team.
          </Alert>
        )}

        {topLevelError !== null && (
          <Alert variant="danger" isInline title="Import failed" className="pf-v6-u-mb-md">
            {topLevelError}
          </Alert>
        )}

        <Form>
          <FormGroup label="Domain name" isRequired fieldId="import-domain-name">
            <TextInput
              id="import-domain-name"
              type="text"
              value={name}
              onChange={(_event, value) => setName(value)}
              isRequired
              validated={fieldErrors.name ? 'error' : 'default'}
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={fieldErrors.name ? 'error' : 'default'}>
                  {fieldErrors.name ?? 'e.g. example.com'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Registrar" isRequired fieldId="import-domain-platform">
            <Select
              id="import-domain-platform"
              isOpen={isPlatformSelectOpen}
              selected={platformId !== null ? String(platformId) : undefined}
              onSelect={onPlatformSelect}
              onOpenChange={(open) => setIsPlatformSelectOpen(open)}
              toggle={(toggleRef) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsPlatformSelectOpen(!isPlatformSelectOpen)}
                  isExpanded={isPlatformSelectOpen}
                  isDisabled={platformsEmpty}
                  aria-label="Select a registrar"
                  isFullWidth
                >
                  {selectedPlatformName}
                </MenuToggle>
              )}
            >
              <SelectList>
                {platforms.map((p) => (
                  <SelectOption key={p.id} value={String(p.id)}>
                    {p.name}
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={fieldErrors.domain_registration_platform_id ? 'error' : 'default'}>
                  {fieldErrors.domain_registration_platform_id ?? 'The registrar where this domain is managed.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Registration date" isRequired fieldId="import-domain-created-at">
            <DatePicker
              appendTo="inline"
              value={createdAt}
              onChange={makeDateChangeHandler(setCreatedAt)}
              dateParse={handleDateParse}
              aria-label="Registration date"
              buttonAriaLabel="Toggle registration date picker"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={fieldErrors.created_at ? 'error' : 'default'}>
                  {fieldErrors.created_at ?? 'When the domain was registered with the registrar.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Expiration date" isRequired fieldId="import-domain-expires-at">
            <DatePicker
              appendTo="inline"
              value={expiresAt}
              onChange={makeDateChangeHandler(setExpiresAt)}
              dateParse={handleDateParse}
              aria-label="Expiration date"
              buttonAriaLabel="Toggle expiration date picker"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={fieldErrors.expires_at ? 'error' : 'default'}>
                  {fieldErrors.expires_at ?? 'When the registration expires.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Grace-window end" fieldId="import-domain-vanishes-at">
            <DatePicker
              appendTo="inline"
              value={vanishesAt}
              onChange={makeDateChangeHandler(setVanishesAt)}
              dateParse={handleDateParse}
              aria-label="Grace-window end"
              buttonAriaLabel="Toggle grace-window end date picker"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={fieldErrors.vanishes_at ? 'error' : 'default'}>
                  {fieldErrors.vanishes_at ??
                    'Optional. When the domain leaves the registrar grace window. Can be left empty and set later.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>
      </>
    );
  };

  return (
    <Modal variant="medium" isOpen={isOpen} onClose={handleClose} aria-label="Import domain">
      <ModalHeader title="Import domain" />
      <ModalBody>{renderBody()}</ModalBody>
      <ModalFooter>
        <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} style={{ width: '100%' }}>
          <FlexItem>
            <Switch
              id="import-domain-add-another"
              label="Add and create another domain"
              isChecked={addAnother}
              onChange={(_e, checked) => setAddAnother(checked)}
              isDisabled={isSubmitting}
            />
          </FlexItem>
          <FlexItem>
            <Button variant="primary" onClick={handleSubmit} isLoading={isSubmitting} isDisabled={!canSubmit}>
              Import
            </Button>
            <Button variant="link" onClick={handleClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
          </FlexItem>
        </Flex>
      </ModalFooter>
    </Modal>
  );
}

export default ImportDomainModal;
